import type { PeopleViewPreferencesPort } from '../../features/people/public';
import { createPeopleViewPreferencesAdapter } from '../adapters/peopleViewPreferencesAdapter';
import { lazy, Suspense, useSyncExternalStore, type ReactNode } from 'react';
import { Box } from '@mui/material';
import { LoadingIndicator } from '../../design-system';
import type { AuthSession } from '../../api/types';
import { canLoadHeydayPage, isHeydayAppManaged, isHeydayFeatureAvailable, type HeydayPage } from '../../config/heydayPageManifest';
import {
  navigateToHeydayPage,
  pageFromPathname,
  subscribeToHeydayLocation,
} from '../../config/heydayRoutes';
import { integrationSettingsRouteAdapter } from '../adapters/integrationSettingsRouteAdapter';
import { absenceSettingsRouteAdapter } from '../adapters/absenceSettingsRouteAdapter';
import { tenantSettingsRouteAdapter } from '../adapters/tenantSettingsRouteAdapter';
import { gettingStartedRouteAdapter } from '../adapters/gettingStartedRouteAdapter';
import { lifecycleRouteAdapter } from '../adapters/lifecycleRouteAdapter';
import { employeeDirectoryRouteAdapter } from '../adapters/employeeDirectoryRouteAdapter';
import { peopleRouteAdapter } from '../adapters/peopleRouteAdapter';
import { workflowRouteAdapter } from '../adapters/workflowRouteAdapter';
import { organizationStudioRouteAdapter } from '../adapters/organizationStudioRouteAdapter';
import { recruitingDetailRouteAdapters, staffingDetailRouteAdapter, staffingPlanDetailRouteAdapter } from '../adapters/collectionDetailRouteAdapter';
import {
  AbsencePage,
  loadAbsenceSettingsPage,
  type AbsencePort,
  type AbsenceSettingsPort,
} from '../../features/absence/public';
import type { BulkOperationsPort, BulkOperationsRoutePort, BulkReferencePort } from '../../features/bulk/public';
import {
  CertificateOperationsPage,
  CertificateSelfServicePage,
  CertificateSettingsPage,
  type CertificatesPort,
} from '../../features/certificates/public';
import { FeatureReleasePage, type FeatureReleasePort, type FeatureReleaseUser } from '../../features/feature-release/public';
import { DocumentsPage, type DocumentsPort } from '../../features/documents/public';
import {
  EmployeeHomePage,
  HomePage,
  type EmployeeHomePort,
  type EmployeeHomeWorkflowSummary,
  type OperationsHomePort,
} from '../../features/home/public';
import { InboxPage, type InboxPort } from '../../features/inbox/public';
import {
  LifecyclePage,
  type LifecyclePort,
} from '../../features/lifecycle/public';
import {
  OrganizationPage,
  type OrganizationPort,
} from '../../features/organization/public';
import {
  EmployeeProfilePage,
  loadDirectoryPage,
  PeoplePage,
  type EmployeeDirectoryPort,
  type PeopleAccess,
  type PeopleOperationsPort,
  type PeopleProfilePort,
} from '../../features/people/public';
import {
  AdministratorManagementPage,
  SecurityAuditPage,
  type AdministratorPort,
} from '../../features/security/public';
import type { EmploymentContractApi } from '../../features/rewards/public';
import { StaffingPage, type StaffingOperationsPort } from '../../features/staffing/public';
import {
  WorkflowRequestCenterPage,
  WorkflowRequestsPage,
  WorkflowTaskInboxPage,
  WorkflowTemplatesPage,
  loadWorkflowOperationsPage,
  type WorkflowApprovalSettingsPort,
  type WorkflowOperationsPort,
  type WorkflowSelfDelegationPort,
  type WorkflowPort,
} from '../../features/workflow/public';
import type { HeadcountPort } from '../../features/headcount/public';
import type { RecruitingPage, RecruitingPort } from '../../features/recruiting/public';
import type { RewardsApi } from '../../features/rewards/public';
import {
  IntegrationSettingsPage,
  type IntegrationSettingsPort,
} from '../../features/integrations/public';
import {
  AffiliatesPage,
  AppearanceSettingsPage,
  CompanyInformationPage,
  CompanySealsPage,
  ReferenceDataPage,
  GettingStartedPage,
  type OrganizationSettings,
  type TenantSettingsPort,
} from '../../features/tenant-settings/public';

const AbsenceSettingsPage = lazy(async () => {
  const module = await loadAbsenceSettingsPage();
  return { default: module.AbsenceSettingsPage };
});
const DirectoryPage = lazy(async () => {
  const module = await loadDirectoryPage();
  return { default: module.DirectoryPage };
});
const RewardsPage = lazy(async () => {
  const rewards = await import('../../features/rewards/public');
  return { default: rewards.RewardsPage };
});
const HeadcountPage = lazy(() => import('../../features/headcount/public').then((module) => ({ default: module.HeadcountPage })));
const WorkflowOperationsPage = lazy(async () => {
  const module = await loadWorkflowOperationsPage();
  return { default: module.WorkflowOperationsPage };
});
const BulkOperationsPage = lazy(() => import('../../features/bulk/public').then((module) => ({ default: module.BulkOperationsPage })));
const RecruitingWorkspacePage = lazy(() => import('../../features/recruiting/public').then((module) => ({ default: module.RecruitingWorkspacePage })));

const peopleBulkOperations = ['EMPLOYEE_HIRE', 'EMPLOYEE_IMPORT', 'EMPLOYEE_REHIRE', 'STAFFING_CHANGE', 'WORKER_LIFECYCLE_CORRECTION'];
const staffingBulkOperations = ['STAFFING_CHANGE', 'WORKER_LIFECYCLE_CORRECTION'];
const absenceBulkOperations = ['TIME_OFF_CORRECTION', 'TIME_OFF_GRANT', 'TIME_OFF_ADJUSTMENT'];

const bulkOperationsRoutePort: BulkOperationsRoutePort = {
  read() {
    const search = new URLSearchParams(window.location.search);
    return {
      active: pageFromPathname(window.location.pathname) === 'bulk',
      hasOperation: search.has('operation'),
      operation: search.get('operation') ?? undefined,
      jobId: search.get('job') ?? undefined,
      source: search.get('source') === 'people' ? 'people' : search.get('source') === 'staffing' ? 'staffing' : undefined,
    };
  },
  clearOperation() {
    if (pageFromPathname(window.location.pathname) !== 'bulk') return;
    const query = new URLSearchParams(window.location.search);
    query.delete('operation');
    navigateToHeydayPage('bulk', query, { replace: true });
  },
  openJob(id) {
    if (pageFromPathname(window.location.pathname) !== 'bulk') return;
    const query = new URLSearchParams(window.location.search);
    query.delete('operation');
    query.set('job', id);
    navigateToHeydayPage('bulk', query);
  },
  closeJob() {
    if (pageFromPathname(window.location.pathname) !== 'bulk') return;
    const query = new URLSearchParams(window.location.search);
    query.delete('job');
    navigateToHeydayPage('bulk', query);
  },
  openOperation(operation) {
    if (pageFromPathname(window.location.pathname) !== 'bulk') return;
    const query = new URLSearchParams(window.location.search);
    query.set('operation', operation);
    navigateToHeydayPage('bulk', query);
  },
  openStaffingChange(id) { navigateToHeydayPage('staffing', new URLSearchParams({ change: id })); },
  returnToSource() {
    navigateToHeydayPage(new URLSearchParams(window.location.search).get('source') === 'staffing' ? 'staffing' : 'people');
  },
  subscribe: subscribeToHeydayLocation,
};

export type HeydayRouteContentProps = {
  currentPage: HeydayPage;
  featureReleasePort: FeatureReleasePort;
  onFeaturePolicySaved: () => void | Promise<void>;
  onFeatureViewAs?: (user: FeatureReleaseUser) => void;
  absencePort: AbsencePort;
  absenceSettingsPort: AbsenceSettingsPort;
  bulkOperationsPort: BulkOperationsPort;
  bulkReferencePort?: BulkReferencePort;
  certificatesPort: CertificatesPort;
  approvalSettingsPort: WorkflowApprovalSettingsPort;
  workflowPort: WorkflowPort;
  workflowOperationsPort: WorkflowOperationsPort;
  workflowSelfDelegationPort: WorkflowSelfDelegationPort;
  headcountPort: HeadcountPort;
  integrationSettingsPort: IntegrationSettingsPort;
  tenantSettingsPort: TenantSettingsPort;
  employeeDirectoryPort: EmployeeDirectoryPort;
  documentsPort: DocumentsPort;
  employeeHomePort: EmployeeHomePort;
  inboxPort: InboxPort;
  operationsHomePort: OperationsHomePort;
  administratorPort: AdministratorPort;
  rewardsApi: RewardsApi;
  contractApi?: EmploymentContractApi;
  staffingOperationsPort: StaffingOperationsPort;
  organizationPort: OrganizationPort;
  peopleOperationsPort: PeopleOperationsPort;
  lifecyclePort: LifecyclePort;
  peopleProfilePort: PeopleProfilePort;
  recruitingPort: RecruitingPort;
  session: AuthSession;
  currentPersonId: string;
  fixtureMode: boolean;
  visiblePages: ReadonlySet<HeydayPage>;
  workflowSummary: EmployeeHomeWorkflowSummary;
  onNavigate: (page: HeydayPage, query?: string) => void;
  onWorkflowTasksChanged: () => void;
  onWorkflowRequestsChanged: () => void;
  onProfilePhotoChanged: () => void;
  onOrganizationChanged: (settings: OrganizationSettings) => void;
};

type HeydayRouteRenderContext = Omit<HeydayRouteContentProps, 'currentPage'> & {
  readOnly: boolean;
  scopes: readonly string[];
  availableFeatures?: readonly string[];
  managementApps?: readonly string[];
};

export type AbsenceSettingsRouteRenderContext = Pick<
  HeydayRouteRenderContext,
  'absenceSettingsPort' | 'fixtureMode' | 'readOnly' | 'scopes' | 'onNavigate' | 'visiblePages'
> & { recoveryScope?: string };

export type AbsenceRouteRenderContext = Pick<
  HeydayRouteRenderContext,
  'absencePort' | 'documentsPort' | 'bulkOperationsPort' | 'bulkReferencePort' | 'fixtureMode' | 'readOnly' | 'scopes' | 'availableFeatures' | 'onNavigate'
> & { workerId: string; personId: string };

export type InboxRouteRenderContext = Pick<
  HeydayRouteRenderContext,
  'inboxPort' | 'workflowPort' | 'approvalSettingsPort' | 'employeeDirectoryPort' | 'fixtureMode' | 'readOnly' | 'scopes'
>;

export type DocumentsRouteRenderContext = Pick<
  HeydayRouteRenderContext,
  'documentsPort' | 'peopleOperationsPort' | 'fixtureMode' | 'readOnly' | 'scopes'
>;

export type OperationsHomeRouteRenderContext = Pick<
  HeydayRouteRenderContext,
  'operationsHomePort' | 'fixtureMode' | 'readOnly' | 'scopes' | 'visiblePages' | 'onNavigate' | 'managementApps'
>;

export type AdministratorRouteRenderContext = Pick<
  HeydayRouteRenderContext,
  'administratorPort' | 'fixtureMode' | 'readOnly' | 'scopes'
> & { personId: string };

export type RewardsRouteRenderContext = Pick<
  HeydayRouteRenderContext,
  'rewardsApi' | 'fixtureMode' | 'readOnly' | 'scopes'
> & { initialWorkerId: string; initialKind?: string };

export type StaffingRouteRenderContext = Pick<
  HeydayRouteRenderContext,
  'staffingOperationsPort' | 'bulkOperationsPort' | 'bulkReferencePort' | 'fixtureMode' | 'readOnly' | 'scopes' | 'onNavigate'
>;

export type PeopleRouteRenderContext = Pick<
  HeydayRouteRenderContext,
  'peopleOperationsPort' | 'peopleProfilePort' | 'staffingOperationsPort' | 'contractApi' | 'bulkOperationsPort' | 'bulkReferencePort' | 'fixtureMode' | 'readOnly' | 'scopes' | 'managementApps'
> & { tenantScopes: readonly string[]; viewPreferences: PeopleViewPreferencesPort; viewerKey: string };

export type OrganizationRouteRenderContext = Pick<
  HeydayRouteRenderContext,
  'organizationPort' | 'staffingOperationsPort' | 'onNavigate' | 'fixtureMode' | 'readOnly' | 'scopes'
>;

export type LifecycleRouteRenderContext = Pick<
  HeydayRouteRenderContext,
  'lifecyclePort' | 'fixtureMode' | 'readOnly' | 'scopes'
>;

export type WorkflowOperationsRouteRenderContext = Pick<
  HeydayRouteRenderContext,
  'workflowOperationsPort' | 'employeeDirectoryPort' | 'fixtureMode' | 'readOnly' | 'scopes'
>;

type WorkflowFamilyRouteRenderContext = Pick<
  HeydayRouteRenderContext,
  'workflowPort' | 'approvalSettingsPort' | 'employeeDirectoryPort' | 'fixtureMode' | 'readOnly' | 'scopes' | 'availableFeatures' | 'managementApps'
>;

export type WorkflowRequestCenterRouteRenderContext = WorkflowFamilyRouteRenderContext & Pick<
  HeydayRouteRenderContext,
  'onWorkflowRequestsChanged' | 'staffingOperationsPort'
> & { requesterWorkerId?: string };

export type WorkflowTaskInboxRouteRenderContext = WorkflowFamilyRouteRenderContext & Pick<
  HeydayRouteRenderContext,
  'onWorkflowTasksChanged' | 'workflowSelfDelegationPort'
> & { currentPersonId: string };

export type WorkflowTemplatesRouteRenderContext = WorkflowFamilyRouteRenderContext;
export type WorkflowRequestsRouteRenderContext = WorkflowFamilyRouteRenderContext & Pick<HeydayRouteRenderContext, 'staffingOperationsPort'>;

function workflowFamilyRouteContext(context: HeydayRouteRenderContext): WorkflowFamilyRouteRenderContext {
  return {
    workflowPort: context.workflowPort,
    approvalSettingsPort: context.approvalSettingsPort,
    employeeDirectoryPort: context.employeeDirectoryPort,
    fixtureMode: context.fixtureMode,
    readOnly: context.readOnly,
    scopes: context.scopes,
    availableFeatures: context.availableFeatures,
    managementApps: context.managementApps,
  };
}

function workflowRequestCenterRouteContext(context: HeydayRouteRenderContext): WorkflowRequestCenterRouteRenderContext {
  return {
    ...workflowFamilyRouteContext(context),
    requesterWorkerId: context.session.principal.workerId,
    staffingOperationsPort: context.staffingOperationsPort,
    onWorkflowRequestsChanged: context.onWorkflowRequestsChanged,
  };
}

type HeydayPageRenderer = (context: HeydayRouteRenderContext) => ReactNode;
type HeydayRouteRegistry = Readonly<Record<HeydayPage, HeydayPageRenderer>>;

function absenceRouteContext(context: HeydayRouteRenderContext): AbsenceRouteRenderContext {
  return {
    absencePort: context.absencePort,
    documentsPort: context.documentsPort,
    bulkOperationsPort: context.bulkOperationsPort,
    bulkReferencePort: context.bulkReferencePort,
    availableFeatures: context.availableFeatures,
    fixtureMode: context.fixtureMode,
    readOnly: context.readOnly,
    scopes: context.scopes,
    onNavigate: context.onNavigate,
    workerId: context.session.principal.workerId,
    personId: context.session.principal.personId,
  };
}

function AbsenceRoute({ context, mode }: { context: AbsenceRouteRenderContext; mode: 'self' | 'operations' }) {
  const search = useSyncExternalStore(subscribeToHeydayLocation, () => window.location.search);
  const canManage = mode === 'operations' && context.scopes.includes('absence.manage');
  const canCancelRequest = !context.readOnly && (context.fixtureMode || context.scopes.includes('absence.write'));
  const canRequest = canCancelRequest && isHeydayFeatureAvailable('absence', context.availableFeatures);
  const requiredBulkScopes = ['absence.read', 'staffing.read', 'absence.manage', 'bulk.read', 'bulk.write'];
  const canReadBulk = mode === 'operations' && (context.fixtureMode || context.scopes.includes('bulk.read'));
  const canBulkCreate = mode === 'operations' && !context.readOnly && (context.fixtureMode || requiredBulkScopes.every((scope) => context.scopes.includes(scope)));
  return <AbsencePage
    key={mode}
    port={context.absencePort}
    evidencePort={{ list: async (workerId, personId) => {
      const results = await Promise.all([
        context.documentsPort.list({ subjectType: 'WORKER', subjectId: workerId }),
        personId ? context.documentsPort.list({ subjectType: 'PERSON', subjectId: personId }) : Promise.resolve([]),
      ]);
      return results.flat();
    }, upload: (workerId, file) => context.documentsPort.upload({ subjectType: 'WORKER', subjectId: workerId, documentType: 'ABSENCE_EVIDENCE', file }) }}
    canReadEvidence={context.fixtureMode || context.scopes.includes('documents.read')}
    canUploadEvidence={!context.readOnly && (context.fixtureMode || context.scopes.includes('documents.write'))}
    workerId={context.workerId}
    personId={context.personId}
    fixtureMode={context.fixtureMode}
    mode={mode}
    canManage={canManage}
    canRequest={canRequest}
    initialWorkerId={mode === 'operations' ? new URLSearchParams(search).get('worker') ?? undefined : undefined}
    initialAsOf={mode === 'operations' ? new URLSearchParams(search).get('asOf') ?? undefined : undefined}
    initialRequestId={new URLSearchParams(search).get('request') ?? undefined}
    requestOnEntry={mode === 'self' && new URLSearchParams(search).get('action') === 'request'}
    onRequestEntryHandled={() => navigateToHeydayPage('my-absence', undefined, { replace: true })}
    canCancelRequest={canCancelRequest}
    canManageMutation={!context.readOnly && canManage}
    canBulkCreate={canBulkCreate}
    onOpenWorkflowRequest={(requestId) => context.onNavigate('my-workflow-requests', new URLSearchParams({ request: requestId }).toString())}
    bulkWorkspace={mode === 'operations' ? {
      title: '휴가 처리 내역', port: context.bulkOperationsPort, referencePort: context.bulkReferencePort,
      fixtureMode: context.fixtureMode, canRead: canReadBulk, canCreate: canBulkCreate, canRun: canBulkCreate, allowedOperationTypes: absenceBulkOperations,
    } : undefined}
    onOpenPolicySettings={() => context.onNavigate('absence-settings')}
  />;
}

function peopleRouteContext(context: HeydayRouteRenderContext): PeopleRouteRenderContext {
  return {
    peopleOperationsPort: context.peopleOperationsPort,
    staffingOperationsPort: context.staffingOperationsPort,
    bulkOperationsPort: context.bulkOperationsPort,
    bulkReferencePort: context.bulkReferencePort,
    viewPreferences: createPeopleViewPreferencesAdapter(context.session.principal.tenant, context.session.principal.personId, context.readOnly),
    viewerKey: `${context.session.principal.tenant}:${context.session.principal.personId}`,
    peopleProfilePort: context.peopleProfilePort,
    contractApi: context.contractApi,
    fixtureMode: context.fixtureMode,
    readOnly: context.readOnly,
    scopes: context.scopes,
    tenantScopes: context.session.principal.tenantScopes ?? [],
    managementApps: context.managementApps,
  };
}

function peopleAccessFor(context: PeopleRouteRenderContext): PeopleAccess {
  const scopes = context.scopes;
  const tenantScopes = context.tenantScopes;
  const fixture = context.fixtureMode;
  const mutable = !context.readOnly;
  const canReadPerson = fixture || scopes.includes('person.read');
  const canWritePerson = mutable && (fixture || (
    scopes.includes('staffing.read')
    && scopes.includes('person.write')
  ));
  return {
    canReadPerson,
    canReadNames: fixture || (canReadPerson && scopes.includes('person.name.read')),
    canReadHomeContact: fixture || (canReadPerson && scopes.some((scope) => scope === 'person.home_contact.read' || scope === 'person.home_contact.masked')),
    canReadHomeContactFull: fixture || (canReadPerson && scopes.includes('person.home_contact.read')),
    canReadWorkContact: fixture || (canReadPerson && scopes.some((scope) => scope === 'person.work_contact.read' || scope === 'person.work_contact.masked')),
    canReadWorkContactFull: fixture || (canReadPerson && scopes.includes('person.work_contact.read')),
    canReadPersonalData: fixture || (canReadPerson && scopes.some((scope) => scope === 'person.personal_data.read' || scope === 'person.personal_data.masked')),
    canReadRewards: isHeydayAppManaged('rewards', context.managementApps) && (fixture || scopes.includes('rewards.read')),
    canReadAbsence: isHeydayAppManaged('absence', context.managementApps) && (fixture || ['absence.read', 'staffing.read'].every(scope => scopes.includes(scope))),
    canReadWorkflowOperations: fixture || ['workflow.request.read.self', 'workflow.request.read.all', 'workflow.template.read'].every(scope => scopes.includes(scope)),
    canWriteRewards: mutable && isHeydayAppManaged('rewards', context.managementApps) && (fixture || scopes.includes('rewards.write')),
    canApproveRewards: mutable && isHeydayAppManaged('rewards', context.managementApps) && (fixture || scopes.includes('rewards.approve')),
    canCancelRewards: mutable && isHeydayAppManaged('rewards', context.managementApps) && (fixture || ['rewards.write', 'business_process.cancel'].every(scope => scopes.includes(scope))),
    canChangeStaffing: mutable && (fixture || ['staffing.read', 'staffing.write'].every(scope => scopes.includes(scope))),
    canWritePerson,
    canEditNames: mutable && (fixture || (
      scopes.includes('staffing.read')
      && scopes.includes('person.write')
      && scopes.includes('person.read')
      && scopes.includes('person.name.read')
      && tenantScopes.includes('person.write')
    )),
    canManageProfilePhoto: canWritePerson && (fixture || tenantScopes.includes('person.write')),
    canReadFamily: fixture || scopes.includes('person.family.read'),
    canWriteFamily: mutable && (fixture || scopes.includes('person.family.write')),
    canApproveFamily: mutable && (fixture || scopes.includes('person.family.approve')),
    canReadCareer: fixture || scopes.includes('person.career.read'),
    canWriteCareer: mutable && (fixture || scopes.includes('person.career.write')),
    canApproveCareer: mutable && (fixture || scopes.includes('person.career.approve')),
    canCreatePerson: mutable && (fixture || (tenantScopes.includes('person.write') && tenantScopes.includes('staffing.write'))),
    canBulkCreate: mutable && (fixture || [
      'staffing.read',
      'person.write',
      'staffing.write',
      'bulk.read',
      'bulk.write',
    ].every((scope) => scopes.includes(scope))),
    canBulkChange: mutable && (fixture || ['staffing.read', 'staffing.write', 'bulk.read', 'bulk.write'].every((scope) => scopes.includes(scope))),
    canExport: mutable && (fixture || scopes.includes('reports.export')),
    canOpenWorkflowRequests: scopes.includes('workflow.request.read.self'),
    canStartOnboarding: mutable && isHeydayAppManaged('lifecycle', context.managementApps) && (fixture || [
      'staffing.read', 'lifecycle.read', 'lifecycle.write',
    ].every((scope) => scopes.includes(scope))),
  };
}

function renderPeople(context: PeopleRouteRenderContext) {
  const access = peopleAccessFor(context);
  const canReadBulk = context.fixtureMode || context.scopes.includes('bulk.read');
  return <PeoplePage
    key={context.viewerKey}
    port={context.peopleOperationsPort}
    staffingPort={context.staffingOperationsPort}
    contractApi={context.contractApi}
    profilePort={context.peopleProfilePort}
    route={peopleRouteAdapter}
    bulkPort={context.bulkOperationsPort}
    bulkWorkspace={{
      title: '구성원 처리 내역', port: context.bulkOperationsPort, referencePort: context.bulkReferencePort,
      fixtureMode: context.fixtureMode, canRead: canReadBulk, canCreate: access.canBulkCreate,
      canRun: Boolean(access.canBulkCreate || access.canBulkChange), allowedOperationTypes: peopleBulkOperations,
      onOpenStaffingChange: context.fixtureMode || context.scopes.includes('staffing.read')
        ? (id) => navigateToHeydayPage('staffing', new URLSearchParams({ change: id })) : undefined,
    }}
    recoveryScope={context.readOnly ? undefined : context.viewerKey}
    preferences={context.readOnly ? undefined : context.viewPreferences}
    access={access}
    fixtureMode={context.fixtureMode}
  />;
}

function renderInbox({ inboxPort, workflowPort, approvalSettingsPort, employeeDirectoryPort, fixtureMode, readOnly, scopes }: InboxRouteRenderContext) {
  return <InboxPage
    port={inboxPort}
    workflowPort={workflowPort}
    approvalSettingsPort={approvalSettingsPort}
    directoryPort={employeeDirectoryPort}
    route={workflowRouteAdapter}
    scopes={scopes}
    fixtureMode={fixtureMode}
    canAct={!readOnly && (fixtureMode || scopes.includes('business_process.approve'))}
  />;
}

function renderDocuments({ documentsPort, peopleOperationsPort, fixtureMode, readOnly, scopes }: DocumentsRouteRenderContext) {
  return <DocumentsPage
    port={documentsPort}
    fixtureMode={fixtureMode}
    canSearchSubjects={{ WORKER: scopes.includes('staffing.read'), PERSON: scopes.includes('person.read') && scopes.includes('person.name.read') }}
    loadSubjects={async (subjectType, query) => {
      if (subjectType === 'PERSON') {
        if (!scopes.includes('person.read') || !scopes.includes('person.name.read')) return [];
        return documentsPort.searchPersonSubjects(query);
      }
      if (!scopes.includes('staffing.read')) return [];
      const asOf = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
      const workers = await peopleOperationsPort.listWorkers(asOf);
      const options = workers.map((worker) => ({
        id: worker.id,
        name: worker.name,
        detail: [worker.title, worker.organization].filter(Boolean).join(' · '),
      })).filter((option) => option.id && `${option.name} ${option.detail}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
      return [...new Map(options.map((option) => [option.id, option])).values()];
    }}
    canUpload={!readOnly && (fixtureMode || scopes.includes('documents.write'))}
    canDownload={!readOnly && (fixtureMode || scopes.includes('documents.read'))}
  />;
}

function renderWorkflowRequestCenter(context: WorkflowRequestCenterRouteRenderContext, view: 'create' | 'requests') {
  return <WorkflowRequestCenterPage
    staffingPort={isHeydayAppManaged('people', context.managementApps) ? context.staffingOperationsPort : undefined}
    port={context.workflowPort}
    route={workflowRouteAdapter}
    approvalSettingsPort={context.approvalSettingsPort}
    directoryPort={context.employeeDirectoryPort}
    fixtureMode={context.fixtureMode}
    readOnly={context.readOnly}
    scopes={context.scopes}
    view={view}
    allowNewRequests={isHeydayFeatureAvailable('workflow', context.availableFeatures)}
    requesterWorkerId={context.requesterWorkerId}
    onRequestsChanged={context.onWorkflowRequestsChanged}
  />;
}

function renderWorkflowTaskInbox({
  workflowPort,
  workflowSelfDelegationPort,
  currentPersonId,
  approvalSettingsPort,
  employeeDirectoryPort,
  fixtureMode,
  readOnly,
  scopes,
  onWorkflowTasksChanged,
}: WorkflowTaskInboxRouteRenderContext) {
  return <WorkflowTaskInboxPage
    selfDelegationPort={workflowSelfDelegationPort}
    currentPersonId={currentPersonId}
    route={workflowRouteAdapter}
    port={workflowPort}
    approvalSettingsPort={approvalSettingsPort}
    directoryPort={employeeDirectoryPort}
    fixtureMode={fixtureMode}
    readOnly={readOnly}
    scopes={scopes}
    onTasksChanged={onWorkflowTasksChanged}
  />;
}

function renderWorkflowTemplates({
  workflowPort,
  approvalSettingsPort,
  employeeDirectoryPort,
  fixtureMode,
  readOnly,
  scopes,
}: WorkflowTemplatesRouteRenderContext, allowGeneralTemplates = true) {
  return <WorkflowTemplatesPage
    allowGeneralTemplates={allowGeneralTemplates}
    port={workflowPort}
    approvalSettingsPort={approvalSettingsPort}
    directoryPort={employeeDirectoryPort}
    fixtureMode={fixtureMode}
    readOnly={readOnly}
    scopes={scopes}
  />;
}

function renderWorkflowRequests({
  workflowPort,
  staffingOperationsPort,
  approvalSettingsPort,
  employeeDirectoryPort,
  fixtureMode,
  readOnly,
  scopes,
  managementApps,
}: WorkflowRequestsRouteRenderContext) {
  return <WorkflowRequestsPage
    staffingPort={isHeydayAppManaged('people', managementApps) ? staffingOperationsPort : undefined}
    canBrowseTemplates={isHeydayAppManaged('workflow', managementApps)}
    port={workflowPort}
    route={workflowRouteAdapter}
    approvalSettingsPort={approvalSettingsPort}
    directoryPort={employeeDirectoryPort}
    fixtureMode={fixtureMode}
    readOnly={readOnly}
    scopes={scopes}
  />;
}

function renderEmployeeDirectory(context: HeydayRouteRenderContext) {
  return <Suspense fallback={<Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}><LoadingIndicator label="조직도 불러오는 중" /></Box>}>
    <DirectoryPage port={context.employeeDirectoryPort} route={employeeDirectoryRouteAdapter} fixtureMode={context.fixtureMode} />
  </Suspense>;
}

function RecruitingRoute({ context, page }: { context: HeydayRouteRenderContext; page: RecruitingPage }) {
  const search = useSyncExternalStore(subscribeToHeydayLocation, () => window.location.search);
  const query = new URLSearchParams(search);
  const filter = query.get('filter');
  return <Suspense fallback={<Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}><LoadingIndicator label="채용 화면 불러오는 중" /></Box>}>
    <RecruitingWorkspacePage
      page={page}
      port={context.recruitingPort}
      detailRoutes={recruitingDetailRouteAdapters}
      fixtureMode={context.fixtureMode}
      readOnly={context.readOnly}
      scopes={context.scopes}
      navigation={{
        jobId: query.get('jobId') || undefined,
        applicationId: query.get('applicationId') || undefined,
        create: query.get('create') === 'true',
        filter: filter === 'overdue' || filter === 'approval-pending' || filter === 'stalled' ? filter : undefined,
      }}
      onNavigate={(destination, state) => {
        const parameters = new URLSearchParams();
        if (state?.jobId) parameters.set('jobId', state.jobId);
        if (state?.applicationId) parameters.set('applicationId', state.applicationId);
        if (state?.create) parameters.set('create', 'true');
        if (state?.filter) parameters.set('filter', state.filter);
        context.onNavigate(destination, parameters.toString());
      }}
    />
  </Suspense>;
}

const tenantSettingsProps = (context: HeydayRouteRenderContext) => ({
  port: context.tenantSettingsPort,
  route: tenantSettingsRouteAdapter,
  fixtureMode: context.fixtureMode,
  access: { scopes: context.scopes, readOnly: context.readOnly },
  onOrganizationChanged: context.onOrganizationChanged,
});

function renderAbsenceSettings(context: AbsenceSettingsRouteRenderContext) {
  return <Suspense fallback={<Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}><LoadingIndicator label="휴가 설정 불러오는 중" /></Box>}>
    <AbsenceSettingsPage
      port={context.absenceSettingsPort}
      recoveryScope={context.recoveryScope}
      route={absenceSettingsRouteAdapter}
      fixtureMode={context.fixtureMode}
      canWrite={!context.readOnly && context.scopes.includes('absence.manage')}
      canReadImpact={context.fixtureMode || context.scopes.includes('absence.manage')}
      canPreviewPolicy={context.fixtureMode || (context.scopes.includes('absence.manage') && context.scopes.includes('staffing.read'))}
      onOpenHolidayImpact={context.visiblePages.has('absence') ? (workerId, requestId) => context.onNavigate('absence', new URLSearchParams({ worker: workerId, request: requestId }).toString()) : undefined}
      onOpenGettingStarted={context.visiblePages.has('admin-getting-started') ? () => context.onNavigate('admin-getting-started', 'item=absence') : undefined}
      onOpenApprovalSettings={context.visiblePages.has('workflow-approval-settings') ? () => context.onNavigate('workflow-approval-settings') : undefined}
    />
  </Suspense>;
}

function renderBulkOperations(context: HeydayRouteRenderContext) {
  const allowedOperationTypes = [
    ...(isHeydayAppManaged('people', context.managementApps) ? ['EMPLOYEE_HIRE', 'EMPLOYEE_IMPORT', 'EMPLOYEE_REHIRE', 'STAFFING_CHANGE', 'WORKER_LIFECYCLE_CORRECTION'] : []),
    ...(isHeydayAppManaged('absence', context.managementApps) ? ['TIME_OFF_CORRECTION', 'TIME_OFF_GRANT', 'TIME_OFF_ADJUSTMENT'] : []),
  ];
  const canCreate = !context.readOnly && context.scopes.includes('bulk.write') && allowedOperationTypes.length > 0;
  const canRun = !context.readOnly && context.scopes.includes('bulk.write');
  return <Suspense fallback={<Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}><LoadingIndicator label="대량 작업 불러오는 중" /></Box>}>
    <BulkOperationsPage
      port={context.bulkOperationsPort}
      referencePort={context.bulkReferencePort}
      allowedOperationTypes={allowedOperationTypes}
      route={bulkOperationsRoutePort}
      fixtureMode={context.fixtureMode}
      canCreate={canCreate}
      canRun={canRun}
      canReadStaffing={context.fixtureMode || (isHeydayAppManaged('people', context.managementApps) && context.scopes.includes('staffing.read'))}
    />
  </Suspense>;
}

function renderWorkflowOperations({
  workflowOperationsPort,
  employeeDirectoryPort,
  fixtureMode,
  readOnly,
  scopes,
}: WorkflowOperationsRouteRenderContext) {
  return <Suspense fallback={<Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}><LoadingIndicator label="결재 운영 불러오는 중" /></Box>}>
    <WorkflowOperationsPage
      port={workflowOperationsPort}
      directoryPort={employeeDirectoryPort}
      canSearchPeople={fixtureMode || scopes.includes('directory.read')}
      fixtureMode={fixtureMode}
      canCreate={!readOnly && scopes.includes('workflow.write')}
      canManagePolicy={!readOnly && scopes.includes('workflow.write') && scopes.includes('security.manage')}
      canRun={!readOnly && scopes.includes('workflow.run')}
    />
  </Suspense>;
}

function renderRewards({
  rewardsApi,
  initialWorkerId,
  initialKind,
  fixtureMode,
  readOnly,
  scopes,
}: RewardsRouteRenderContext) {
  return <Suspense fallback={<Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}><LoadingIndicator label="보상 화면 불러오는 중" /></Box>}>
    <RewardsPage
      api={rewardsApi}
      initialWorkerId={initialWorkerId}
      initialKind={initialKind}
      fixtureMode={fixtureMode}
      canCreate={!readOnly && scopes.includes('rewards.write')}
      canSearchWorkers={fixtureMode || scopes.includes('staffing.read')}
    />
  </Suspense>;
}

function renderHeadcount(context: HeydayRouteRenderContext) {
  const canCreate = !context.readOnly
    && context.scopes.includes('staffing.write')
    && context.scopes.includes('headcount.write');
  return <Suspense fallback={<Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}><LoadingIndicator label="정원 화면 불러오는 중" /></Box>}>
    <HeadcountPage port={context.headcountPort} fixtureMode={context.fixtureMode} canCreate={canCreate}
      canSearchOrganizations={context.fixtureMode || context.scopes.includes('staffing.read')}
      loadOrganizations={async (asOf) => {
        if (!context.scopes.includes('staffing.read')) return [];
        const organizations = await context.organizationPort.listOrganizations(asOf);
        const byId = new Map(organizations.map((organization) => [organization.id, organization]));
        return organizations.map((organization) => {
          const ancestors: string[] = [];
          const seen = new Set([organization.id]);
          let parentId = organization.parentId;
          while (parentId && !seen.has(parentId)) {
            seen.add(parentId);
            const parent = byId.get(parentId);
            if (!parent) break;
            ancestors.unshift(parent.name);
            parentId = parent.parentId;
          }
          return { id: organization.id, name: organization.name, path: [...ancestors, organization.name].join(' / ') };
        });
      }}
    />
  </Suspense>;
}

function renderOperationsHome({
  operationsHomePort,
  fixtureMode,
  readOnly,
  scopes,
  visiblePages,
  onNavigate,
  managementApps,
}: OperationsHomeRouteRenderContext) {
  return <HomePage
    port={operationsHomePort}
    access={{
      canReadReports: visiblePages.has('people') && isHeydayAppManaged('absence', managementApps) && (fixtureMode || scopes.includes('reports.read')),
      canReadStaffing: visiblePages.has('organization') && (fixtureMode || scopes.includes('staffing.read')),
      canAccessPeople: visiblePages.has('people'),
      canReadTenantSettings: fixtureMode || scopes.includes('tenant.settings.read'),
      canAccessInbox: visiblePages.has('inbox'),
      canExportReports: visiblePages.has('people') && !readOnly && (fixtureMode || scopes.includes('reports.export')),
    }}
    onNavigate={onNavigate}
    visiblePages={visiblePages}
    fixtureMode={fixtureMode}
  />;
}

function renderAdministrator({
  administratorPort,
  personId,
  fixtureMode,
  readOnly,
  scopes,
}: AdministratorRouteRenderContext) {
  return <AdministratorManagementPage
    port={administratorPort}
    viewer={{ personId }}
    fixtureMode={fixtureMode}
    canManage={!readOnly && (fixtureMode || scopes.includes('security.manage'))}
    canReadStaffing={fixtureMode || scopes.includes('staffing.read')}
  />;
}

function renderOrganization({
  organizationPort,
  staffingOperationsPort,
  onNavigate,
  fixtureMode,
  readOnly,
  scopes,
}: OrganizationRouteRenderContext) {
  return <OrganizationPage
    port={organizationPort}
    studioRoute={organizationStudioRouteAdapter}
    planningPort={!readOnly && (fixtureMode || (scopes.includes('staffing.read') && scopes.includes('staffing.write'))) ? staffingOperationsPort : undefined}
    onOpenPlan={(planId) => onNavigate('staffing', new URLSearchParams({ planId }).toString())}
    onOpenPlans={fixtureMode || scopes.includes('staffing.read') ? () => onNavigate('staffing') : undefined}
    fixtureMode={fixtureMode}
    canWrite={!readOnly && (fixtureMode || scopes.includes('staffing.write'))}
    canCancelHistory={!readOnly && (fixtureMode || scopes.includes('business_process.cancel'))}
  />;
}

function renderLifecycle({
  lifecyclePort,
  fixtureMode,
  readOnly,
  scopes,
}: LifecycleRouteRenderContext) {
  return <LifecyclePage
    port={lifecyclePort}
    route={lifecycleRouteAdapter}
    fixtureMode={fixtureMode}
    canMutate={!readOnly && (fixtureMode || (
      scopes.includes('lifecycle.read')
      && scopes.includes('staffing.read')
      && scopes.includes('lifecycle.write')
    ))}
  />;
}

function renderStaffing({
  staffingOperationsPort,
  bulkOperationsPort,
  bulkReferencePort,
  fixtureMode,
  readOnly,
  scopes,
  onNavigate,
}: StaffingRouteRenderContext) {
  const canReadBulk = fixtureMode || scopes.includes('bulk.read');
  const canBulkCreate = !readOnly && (fixtureMode || ['staffing.read', 'staffing.write', 'bulk.read', 'bulk.write'].every((scope) => scopes.includes(scope)));
  return <StaffingPage
    port={staffingOperationsPort}
    detailRoute={staffingDetailRouteAdapter}
    planDetailRoute={staffingPlanDetailRouteAdapter}
    navigation={{
      openPlanEditor: !readOnly && (fixtureMode || (scopes.includes('staffing.read') && scopes.includes('staffing.write'))) ? (planId, view, options) => {
        const query = new URLSearchParams({ planId, view });
        if (options?.replace) navigateToHeydayPage('organization', query, { replace: true });
        else onNavigate('organization', query.toString());
      } : undefined,
      openWorkflowRequest: (workflow) => onNavigate('my-workflow-requests', new URLSearchParams({ request: workflow.id }).toString()),
      openBulkChange: () => onNavigate('bulk', 'operation=STAFFING_CHANGE'),
    }}
    bulkWorkspace={{
      title: '인사 발령 처리 내역', port: bulkOperationsPort, referencePort: bulkReferencePort,
      fixtureMode, canRead: canReadBulk, canCreate: canBulkCreate, canRun: canBulkCreate, allowedOperationTypes: staffingBulkOperations,
      onOpenStaffingChange: fixtureMode || scopes.includes('staffing.read')
        ? (id) => onNavigate('staffing', new URLSearchParams({ change: id }).toString()) : undefined,
    }}
    access={{
      canMutate: !readOnly && (fixtureMode || scopes.includes('staffing.write')),
      canCancelLifecycle: !readOnly && (fixtureMode || (scopes.includes('staffing.write') && scopes.includes('business_process.cancel'))),
      canBulkCreate,
      canOpenWorkflowRequests: scopes.includes('workflow.request.read.self'),
    }}
    fixtureMode={fixtureMode}
  />;
}

// App layer가 canonical page key와 실제 화면 조립을 함께 소유한다. HeydayPage가
// 늘거나 줄면 이 registry가 compile-time에 누락과 불필요한 renderer를 모두 검출한다.
export const canonicalHeydayRouteRegistry = {
  'my-home': (context) => <EmployeeHomePage
    port={context.employeeHomePort}
    viewer={{
      tenant: context.session.principal.tenant,
      personId: context.session.principal.personId,
      workerId: context.session.principal.workerId,
      workerReference: isHeydayFeatureAvailable('people', context.availableFeatures) ? undefined : 'me',
      impersonating: Boolean(context.session.impersonation),
    }}
    fixtureMode={context.fixtureMode}
    visiblePages={context.visiblePages}
    workflowSummary={context.workflowSummary}
    onNavigate={context.onNavigate}
  />,
  'my-tasks': (context) => renderWorkflowTaskInbox({
    ...workflowFamilyRouteContext(context),
    onWorkflowTasksChanged: context.onWorkflowTasksChanged,
    workflowSelfDelegationPort: context.workflowSelfDelegationPort,
    currentPersonId: context.currentPersonId,
  }),
  'my-workflows': (context) => renderWorkflowRequestCenter(workflowRequestCenterRouteContext(context), 'create'),
  'my-workflow-requests': (context) => renderWorkflowRequestCenter(workflowRequestCenterRouteContext(context), 'requests'),
  'my-profile': (context) => <EmployeeProfilePage
    port={context.peopleProfilePort}
    viewer={{
      workerId: context.session.principal.workerId,
      workerReference: isHeydayFeatureAvailable('people', context.availableFeatures) ? undefined : 'me',
      scopes: context.session.principal.scopes,
      impersonating: context.readOnly,
      rewardsAvailable: isHeydayFeatureAvailable('rewards', context.availableFeatures),
    }}
    fixtureMode={context.fixtureMode}
    onProfilePhotoChanged={context.onProfilePhotoChanged}
  />,
  'my-absence': (context) => <AbsenceRoute context={absenceRouteContext(context)} mode="self" />,
  'my-documents': (context) => <CertificateSelfServicePage port={context.certificatesPort} fixtureMode={context.fixtureMode} readOnly={context.readOnly} scopes={context.scopes} />,
  directory: renderEmployeeDirectory,
  home: renderOperationsHome,
  people: (context) => renderPeople(peopleRouteContext(context)),
  organization: renderOrganization,
  staffing: renderStaffing,
  lifecycle: renderLifecycle,
  inbox: renderInbox,
  absence: (context) => <AbsenceRoute context={absenceRouteContext(context)} mode="operations" />,
  'absence-settings': (context) => renderAbsenceSettings({ ...context, recoveryScope: `${context.session.principal.tenant}:${context.session.principal.personId}` }),
  rewards: (context) => renderRewards({
    rewardsApi: context.rewardsApi,
    initialWorkerId: context.fixtureMode || context.scopes.includes('rewards.read') ? new URLSearchParams(window.location.search).get('worker') ?? context.session.principal.workerId ?? '' : context.session.principal.workerId ?? '',
    initialKind: new URLSearchParams(window.location.search).get('kind') ?? undefined,
    fixtureMode: context.fixtureMode,
    readOnly: context.readOnly,
    scopes: context.scopes,
  }),
  headcount: renderHeadcount,
  workflow: renderWorkflowOperations,
  'workflow-approval-settings': (context) => renderWorkflowTemplates(workflowFamilyRouteContext(context), false),
  'workflow-templates': (context) => renderWorkflowTemplates(workflowFamilyRouteContext(context)),
  'workflow-requests': (context) => renderWorkflowRequests({ ...workflowFamilyRouteContext(context), staffingOperationsPort: context.staffingOperationsPort }),
  bulk: renderBulkOperations,
  documents: renderDocuments,
  certificates: (context) => <CertificateOperationsPage port={context.certificatesPort} fixtureMode={context.fixtureMode} readOnly={context.readOnly} scopes={context.scopes} />,
  'recruiting-home': (context) => <RecruitingRoute context={context} page="recruiting-home" />,
  requisitions: (context) => <RecruitingRoute context={context} page="requisitions" />,
  candidates: (context) => <RecruitingRoute context={context} page="candidates" />,
  pipeline: (context) => <RecruitingRoute context={context} page="pipeline" />,
  interviews: (context) => <RecruitingRoute context={context} page="interviews" />,
  offers: (context) => <RecruitingRoute context={context} page="offers" />,
  'recruiting-reports': (context) => <RecruitingRoute context={context} page="recruiting-reports" />,
  'admin-company-info': (context) => <CompanyInformationPage {...tenantSettingsProps(context)} />,
  'admin-getting-started': (context) => <GettingStartedPage port={context.tenantSettingsPort} route={gettingStartedRouteAdapter} fixtureMode={context.fixtureMode} access={{ scopes: context.scopes, readOnly: context.readOnly }} absenceAvailable={isHeydayAppManaged('absence', context.managementApps)} availableDestinations={gettingStartedRouteAdapter.availableDestinations(context.visiblePages)} onOpenFeatureSettings={context.visiblePages.has('admin-features') ? () => context.onNavigate('admin-features') : undefined} />,
  'admin-affiliates': (context) => <AffiliatesPage {...tenantSettingsProps(context)} />,
  'admin-company-seals': (context) => <CompanySealsPage {...tenantSettingsProps(context)} />,
  'admin-reference-data': (context) => <ReferenceDataPage {...tenantSettingsProps(context)} />,
  'admin-appearance': (context) => <AppearanceSettingsPage {...tenantSettingsProps(context)} />,
  'admin-features': (context) => <FeatureReleasePage port={context.featureReleasePort} fixtureMode={context.fixtureMode} canManage={!context.readOnly && (context.fixtureMode || context.scopes.includes('security.manage'))} onSaved={context.onFeaturePolicySaved} onViewAs={context.onFeatureViewAs} />,
  'admin-security': (context) => renderAdministrator({
    administratorPort: context.administratorPort,
    personId: context.session.principal.personId,
    fixtureMode: context.fixtureMode,
    readOnly: context.readOnly,
    scopes: context.scopes,
  }),
  'admin-integrations': (context) => <IntegrationSettingsPage
    port={context.integrationSettingsPort}
    routePort={integrationSettingsRouteAdapter}
    onOpenHolidayCalendars={canLoadHeydayPage('absence-settings', context) ? () => context.onNavigate('absence-settings', 'view=calendars') : undefined}
    fixtureMode={context.fixtureMode}
    canWrite={!context.readOnly && (context.fixtureMode || context.scopes.includes('tenant.settings.write'))}
    readOnly={context.readOnly}
  />,
  'admin-certificates': (context) => <CertificateSettingsPage port={context.certificatesPort} fixtureMode={context.fixtureMode} readOnly={context.readOnly} scopes={context.scopes} onOpenCompanySettings={() => context.onNavigate('admin-affiliates')} />,
  'admin-audit': (context) => (
    <SecurityAuditPage viewer={{
      tenant: context.session.principal.tenant,
      principalType: context.session.principal.type,
      roleCount: context.session.principal.roles.length,
      scopeCount: context.session.principal.scopes.length,
    }} />
  ),
} satisfies HeydayRouteRegistry;

export function HeydayRouteContent({ currentPage, ...props }: HeydayRouteContentProps) {
  const context: HeydayRouteRenderContext = {
    ...props,
    readOnly: Boolean(props.session.impersonation),
    scopes: props.session.principal.scopes,
    availableFeatures: props.session.principal.availableFeatures,
    managementApps: props.session.principal.managementApps,
  };
  return canonicalHeydayRouteRegistry[currentPage](context);
}
